import React, { useContext, useEffect } from 'react';
import { BrowserRouter, Switch, Route } from 'react-router-dom';
import { createMuiTheme, ThemeProvider } from '@material-ui/core';
import { AuthStatusContext } from '@trp-individual-investor-adv-disc/adv-shared';

import AppWrapper from '../../components/AppWrapper/AppWrapper';
import TransferWrapper from '../../components/TransferWrapper/TransferWrapper';
import InvestmentProposalContainer from '../InvestmentProposalContainer/InvestmentProposalContainer';
import TransferContainer from '../TransferContainer/TransferContainer';
import ErrorContainer from '../ErrorContainer/ErrorContainer';
import StatusHub from '../../components/StatusHub/StatusHub';
import NoMatch from '../NoMatch/NoMatch';

const App = () => {

    const theme = createMuiTheme({
        typography: {
            fontFamily: 'Nimbus Sans Reg'
        },
        palette: {
            text: {
                primary: '#3B4B59'
            }
        },
        overrides: {
            MuiPaper: {
                rounded: {
                    borderRadius: 0
                },
                elevation1: {
                    boxShadow: '0px 3px 3px rgba(59, 59, 59, 0.05), 0px 6px 4px rgba(59, 59, 59, 0.05), 0px 0px 8px rgba(59, 59, 59, 0.12)'
                }
            },
            MuiButton: {
                root: {
                    fontSize: 'inherit',
                    textTransform: 'none',
                    fontWeight: 'bold',
                    color: '#086EA1',
                    height: '32px',
                    lineHeight: '16px'
                },
                containedPrimary: {
                    backgroundColor: '#086EA1'
                },
                outlinedPrimary: {
                    color: '#086EA1',
                    borderColor: '#086EA1',
                    '&:hover': {
                        // color: '#FFFFFF',
                        backgroundColor: '#086EA1'
                    }
                }
            },
            MuiFormControl: {
                root: {
                    paddingBottom: '32px'
                }
            },
            MuiOutlinedInput: {
                root: {
                    height: '64px',
                    '&$error': {
                        color: '#DD3636',
                    },
                },
                input: {
                    padding: '32px 16px 16px 16px',
                    color: '#253746',
                    fontSize: '16px',
                    lineHeight: '22px',
                    '&$error': {
                        color: '#DD3636',
                    },
                },
            },
            MuiFormLabel: {
                root: {
                    '&$error': {
                        color: '#DD3636'
                    }
                }
            },
            MuiFormHelperText: {
                root: {
                    '&$error': {
                        color: '#DD3636'
                    }
                }
            },
            MuiSelect: {
                icon: {
                    color: '#253746'
                }
            },
            MuiRadio: {
                root: {
                    color: '#086EA1',
                    '&$checked': {
                        color: '#086EA1'
                    }
                },
                colorPrimary: {
                    color: '#086EA1',
                    '&$checked': {
                        color: '#086EA1'
                    }
                }
            },
            MuiCheckbox: {
                root: {
                    color: '#086EA1',
                    '&$checked': {
                        color: '#086EA1'
                    }
                },
                colorPrimary: {
                    color: '#086EA1',
                    '&$checked': {
                        color: '#086EA1'
                    }
                },
                colorSecondary: {
                    color: '#007DB3',
                    '&$checked': {
                        color: '#007DB3'
                    },
                    paddingTop: '6px',
                    alignSelf: 'start'
                }
            },
            MuiExpansionPanel: {
                root: {
                    margin: '0 -32px',
                    '&$expanded': {
                        margin: '0 -32px'
                    }
                }
            },
            MuiExpansionPanelSummary: {
                root: {
                    padding: '16px 32px'
                },
                content: {
                    padding: 0,
                    margin: 0
                }
            },
            MuiExpansionPanelDetails: {
                root: {
                    padding: '16px 32px 32px 32px'
                }
            },
            MuiTableCell: {
                root: {
                    fontSize: '1rem'
                }
            },
            MuiSvgIcon: {
                root: {
                    fontSize: 'inherit'
                }
            }
        }
    });

    const engagementType = typeId => {
        switch (true) {
            case typeId === 5269:
                return 'RAS-Onboarding';
            case typeId === 5270:
                return 'RAS-Add Account';
            case typeId === 5271:
                return 'RAS-Review';
            default:
                return 'RAS-Undetermined/Add Cash';
        }
    }

    const proposalTypeStr = proposalTypeId => {
        switch (true) {
            case proposalTypeId === 5393:
                return 'New Accounts Only';
            case proposalTypeId === 5394:
                return 'Model & New Accounts';
            case proposalTypeId === 5395:
                return 'Model Only';
            case proposalTypeId === 5396:
                return 'Account Opening';
            default:
                return null;
        }
    }

    const { status } = useContext(AuthStatusContext).state;
    const { client, clientHasRAS, coClientHasRAS, engagementTypeId, proposalType } = status;
    useEffect(() => {
        let clientType;
        let hasRAS;
        if (client && client === 'PRIMARY') {
            clientType = 'client';
            hasRAS = clientHasRAS;
        } else {
            clientType = 'co-client';
            hasRAS = coClientHasRAS;
        }
        const isTransferPage = window.location.href.toLowerCase().indexOf('/usis/advice/checkout/transfer') !== -1;
        const clientMetrics = document.createElement('script');
        clientMetrics.type = 'text/javascript';
        clientMetrics.innerHTML = `
            window.metricsData.user = window.metricsData.user || {};
            window.metricsData.user.customerType = 'customer';
            window.metricsData.ras = window.metricsData.ras || {};
            window.metricsData.ras.clientType = '${clientType}';
            window.metricsData.ras.isActive  = '${hasRAS}';
            ${!isTransferPage ?
                `window.metricsData.ras.engagementType  = '${engagementType(engagementTypeId)}';`
            : ''}
            ${!isTransferPage ?
                `window.metricsData.ras.proposalType  = '${proposalTypeStr(proposalType)}';`
            : ''}
        `
        document.body.appendChild(clientMetrics);
        // eslint-disable-next-line
    }, [client]);

    const WrappedHub = ({ location }) => <InvestmentProposalContainer location={location} />;

    const WrappedError = ({ match }) => {
        const useTransferWrapper = window.location.pathname.includes('checkout/error/transfer');

        return useTransferWrapper
            ? <TransferWrapper>
                <ErrorContainer match={match} />
            </TransferWrapper>
            : <AppWrapper>
                <ErrorContainer match={match} />
            </AppWrapper>
    };

    const WrappedStatusHub = () =>
        <AppWrapper>
            <StatusHub status={status} />
        </AppWrapper>;

    return (
        <ThemeProvider theme={theme}>
            <BrowserRouter basename="/usis/advice/checkout">
                <Switch>
                    <Route path="/hub" component={WrappedHub} />
                    <Route path="/transfer" component={TransferContainer} />
                    <Route path="/status" component={WrappedStatusHub} />
                    <Route path="/error/:redirect?" render={({ match }) => <WrappedError match={match} />} />
                    <Route component={NoMatch} />
                </Switch>
            </BrowserRouter>
        </ThemeProvider>
    )
};

export default App;
